/**
 * Production availability for the Reports feature.
 *
 * The reports UI was designed in Lovable preview against seeded dummy data
 * (`dummyReports.ts` generates pseudo-random numbers from the filter values).
 * A template only becomes real once its backend aggregation resolver ships, so
 * production must render nothing it cannot back with live data — otherwise
 * fabricated numbers reach real users.
 *
 * Preview keeps every surface visible so the feature stays designable.
 *
 * Flip an entry to `true` in the same PR that lands its data path.
 */

import { isLovablePreview } from "@/lib/utils/environment";
import { isRecordListBlockId } from "./recordList";
import type { ReportTemplate } from "./types";

export type StandardTemplate = Exclude<ReportTemplate, "custom">;

/** Sub-features that are gated independently of any one template. */
export type ReportSurface =
  /** Scheduled delivery by email/Slack — the UI persists to localStorage and nothing sends. */
  | "scheduleDelivery"
  /** Channel spend and ROI columns — no cost data exists anywhere in the CRM. */
  | "leadSourceCost"
  /** Natural-language "describe a report" box. */
  | "promptToReport";

export interface ReportAvailability {
  templates: Record<StandardTemplate, boolean>;
  /** Record-list blocks, which read real entity data rather than an aggregation. */
  recordLists: boolean;
  surfaces: Record<ReportSurface, boolean>;
}

/**
 * All `false` since the Reports v2 page cutover: v1 was never used in
 * production, and production now runs exclusively on the v2 saved-report path
 * (gated by `FEATURE_FLAGS.REPORTS`). The v1 components stay preview-only —
 * every helper below still returns true under `isLovablePreview()`, so Lovable
 * keeps the full dummy-data experience. Legacy v1 report favorites hide
 * automatically in FavoritesPage via `isSavedReportAvailable`; they are not
 * migrated.
 */
export const REPORT_AVAILABILITY: ReportAvailability = {
  templates: {
    // The pipeline-health data path (`pipelineHealthReport`, coffee #4147,
    // adapter `src/lib/hooks/use-pipeline-health.ts`) still exists but the v1
    // template no longer ships; v2's templates cover it.
    "pipeline-health": false,
    "forecast-accuracy": false,
    "rep-performance": false,
    "lead-source-roi": false,
    "churn-expansion": false,
  },
  // The v1 record-list engine (grid-query-backed) is preview-only now; v2
  // record_list blocks are executed server-side and rendered from run
  // snapshots.
  recordLists: false,
  surfaces: {
    scheduleDelivery: false,
    leadSourceCost: false,
    // Off with the rest of v1: the drafting agent emits v1 template/block
    // drafts. Prompt→report returns when drafting v2 is wired to
    // `createReport` (phase C2c). The client-side regex remains the preview
    // path.
    promptToReport: false,
  },
};

/**
 * Block id → the template whose data builder backs it.
 *
 * Kept here as plain data so availability checks stay free of the block
 * registry, which pulls in the chart components. `availability.test.ts` asserts
 * this map agrees with `BLOCK_REGISTRY`, so the two cannot drift.
 */
export const BLOCK_SOURCES: Record<string, StandardTemplate> = {
  "kpis-pipeline": "pipeline-health",
  "kpis-forecast": "forecast-accuracy",
  "kpis-rep": "rep-performance",
  "kpis-leadsource": "lead-source-roi",
  "kpis-retention": "churn-expansion",

  "summary-pipeline": "pipeline-health",
  "summary-forecast": "forecast-accuracy",
  "summary-rep": "rep-performance",
  "summary-leadsource": "lead-source-roi",
  "summary-retention": "churn-expansion",

  "chart-pipeline-funnel": "pipeline-health",
  "chart-forecast-trend": "forecast-accuracy",
  "chart-leadsource-funnel": "lead-source-roi",
  "chart-retention-trend": "churn-expansion",

  "table-open-opps": "pipeline-health",
  "table-stage-conversion": "pipeline-health",
  "table-forecast-reps": "forecast-accuracy",
  "table-rep-leaderboard": "rep-performance",
  "table-leadsource": "lead-source-roi",
  "table-renewals": "churn-expansion",
};

/** True when the template renders real data (always true in preview). */
export function isTemplateAvailable(template: ReportTemplate): boolean {
  if (isLovablePreview()) return true;
  if (template === "custom") return hasAnyAvailableBlock();
  return REPORT_AVAILABILITY.templates[template] ?? false;
}

/** True when at least one block kind can be built from real data. */
export function hasAnyAvailableBlock(): boolean {
  if (isLovablePreview()) return true;
  return REPORT_AVAILABILITY.recordLists || Object.values(REPORT_AVAILABILITY.templates).some(Boolean);
}

/** True when a custom-report block id renders real data. */
export function isBlockIdAvailable(id: string): boolean {
  if (isLovablePreview()) return true;
  if (isRecordListBlockId(id)) return REPORT_AVAILABILITY.recordLists;
  const source = BLOCK_SOURCES[id];
  return source ? REPORT_AVAILABILITY.templates[source] : false;
}

/** Drop blocks that cannot be backed yet, preserving order. */
export function filterAvailableBlockIds(ids: string[]): string[] {
  if (isLovablePreview()) return ids;
  return ids.filter(isBlockIdAvailable);
}

/** Templates that should appear in pickers and listings. */
export function availableTemplates(templates: StandardTemplate[]): StandardTemplate[] {
  if (isLovablePreview()) return templates;
  return templates.filter(isTemplateAvailable);
}

/**
 * True when a saved report can still be opened.
 *
 * Custom reports survive as long as one of their blocks is available — the
 * unavailable ones are dropped at render time by `filterAvailableBlockIds`.
 * Saved reports that fail this check are hidden rather than deleted, so they
 * come back when their backend lands.
 */
export function isSavedReportAvailable(report: {
  reportTemplate?: ReportTemplate;
  customBlocks?: string[];
}): boolean {
  if (isLovablePreview()) return true;
  const template = report.reportTemplate;
  if (!template) return false;
  if (template === "custom") return filterAvailableBlockIds(report.customBlocks ?? []).length > 0;
  return isTemplateAvailable(template);
}

/** True when a gated sub-feature should render. */
export function isSurfaceAvailable(surface: ReportSurface): boolean {
  if (isLovablePreview()) return true;
  return REPORT_AVAILABILITY.surfaces[surface] ?? false;
}
